import { useState } from 'react';
import { Camera, ClipboardList, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  formatDateTime,
  type DepotContainerVisit,
  type DepotEirRecord,
  type DepotInspection,
  useDepotList,
} from './depot-hooks';
import {
  DepotEmptyRow,
  DepotGridPanel,
  depotTableClassName,
  depotTableHeadClassName,
  depotTdClassName,
  depotThClassName,
} from './depot-grid';

type EirFormState = {
  visitId: string;
  depotId: string;
  eirType: 'inbound' | 'outbound';
  conditionGrade: string;
  signedBy: string;
  photoCount: string;
};

type InspectionFormState = {
  visitId: string;
  depotId: string;
  inspectionType: 'inbound' | 'outbound' | 'yard';
  conditionGrade: string;
  damageStatus: 'sound' | 'damaged' | 'repair_required' | 'wash_required';
  damageSummary: string;
  inspectedBy: string;
  photoCount: string;
};

const EMPTY_EIR: EirFormState = {
  visitId: '',
  depotId: '',
  eirType: 'inbound',
  conditionGrade: '',
  signedBy: '',
  photoCount: '0',
};

const EMPTY_INSPECTION: InspectionFormState = {
  visitId: '',
  depotId: '',
  inspectionType: 'yard',
  conditionGrade: '',
  damageStatus: 'sound',
  damageSummary: '',
  inspectedBy: '',
  photoCount: '0',
};

export default function DepotInspectionsPage() {
  const { items: eirs, error, refresh } = useDepotList<DepotEirRecord>('/depot/eir?limit=50');
  const { items: inspections, refresh: refreshInspections } = useDepotList<DepotInspection>('/depot/inspections?limit=50');
  const { items: visits, refresh: refreshVisits } = useDepotList<DepotContainerVisit>('/depot/containers?limit=100');
  const [eirOpen, setEirOpen] = useState(false);
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const [eirForm, setEirForm] = useState<EirFormState>(EMPTY_EIR);
  const [inspectionForm, setInspectionForm] = useState<InspectionFormState>(EMPTY_INSPECTION);
  const [saving, setSaving] = useState(false);

  const pageError: DataErrorDetail | null = error
    ? { endpoint: '/depot/eir', message: error, status: 500, code: 'DEPOT_INSPECTIONS_LOAD_FAILED' }
    : null;

  const handleCreateEir = async () => {
    if (!eirForm.visitId || !eirForm.depotId) {
      toast.error('Visit and depot are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/depot/eir', {
        visitId: eirForm.visitId,
        depotId: eirForm.depotId,
        eirType: eirForm.eirType,
        conditionGrade: eirForm.conditionGrade.trim() || undefined,
        signedBy: eirForm.signedBy.trim() || undefined,
        photoCount: Number(eirForm.photoCount) || 0,
      });
      toast.success('EIR captured');
      setEirOpen(false);
      setEirForm(EMPTY_EIR);
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create EIR');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateInspection = async () => {
    if (!inspectionForm.visitId || !inspectionForm.depotId) {
      toast.error('Visit and depot are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/depot/inspections', {
        visitId: inspectionForm.visitId,
        depotId: inspectionForm.depotId,
        inspectionType: inspectionForm.inspectionType,
        conditionGrade: inspectionForm.conditionGrade.trim() || undefined,
        damageStatus: inspectionForm.damageStatus,
        damageSummary: inspectionForm.damageSummary.trim() || undefined,
        inspectedBy: inspectionForm.inspectedBy.trim() || undefined,
        photoCount: Number(inspectionForm.photoCount) || 0,
      });
      toast.success('Inspection recorded');
      setInspectionOpen(false);
      setInspectionForm(EMPTY_INSPECTION);
      await Promise.all([refreshInspections(), refreshVisits()]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create inspection');
    } finally {
      setSaving(false);
    }
  };

  const visitOptions = visits.filter((visit) => visit.visit_status !== 'completed');

  return (
    <div className="space-y-5">
      {pageError && <DataErrorBanner error={pageError} entityLabel="Depot EIR & Inspection" onRetry={refresh} />}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">EIR & Inspection Desk</h2>
          <p className="text-sm text-muted-foreground">
            Capture EIR, inspection outcome, damage status, and photo evidence count.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setEirOpen(true)}>
            <Camera className="mr-1.5 h-4 w-4" />
            Capture EIR
          </Button>
          <Button onClick={() => setInspectionOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            Record Inspection
          </Button>
        </div>
      </div>

      <DepotGridPanel title="Recent EIR" subtitle="Equipment interchange records in row format for fast review.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>EIR</th>
              <th className={depotThClassName}>Type</th>
              <th className={depotThClassName}>Container</th>
              <th className={depotThClassName}>Grade</th>
              <th className={depotThClassName}>Signed By</th>
              <th className={depotThClassName}>Photos</th>
              <th className={depotThClassName}>Signed At</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {eirs.length === 0 ? (
              <DepotEmptyRow colSpan={7}>No EIR records yet.</DepotEmptyRow>
            ) : (
              eirs.map((item) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-mono font-semibold`}>{item.eir_no}</td>
                  <td className={depotTdClassName}><Badge variant="outline">{item.eir_type}</Badge></td>
                  <td className={depotTdClassName}>{item.container_no}</td>
                  <td className={depotTdClassName}>{item.condition_grade ?? '—'}</td>
                  <td className={depotTdClassName}>{item.signed_by ?? 'Unsigned'}</td>
                  <td className={depotTdClassName}>{item.photo_count}</td>
                  <td className={`${depotTdClassName} text-xs text-muted-foreground`}>{formatDateTime(item.signed_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <DepotGridPanel title="Recent Inspection" subtitle="Damage status, grade, photo count, and inspector.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Inspection</th>
              <th className={depotThClassName}>Container</th>
              <th className={depotThClassName}>Type</th>
              <th className={depotThClassName}>Damage</th>
              <th className={depotThClassName}>Grade</th>
              <th className={depotThClassName}>Photos</th>
              <th className={depotThClassName}>Inspector</th>
              <th className={depotThClassName}>Summary</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {inspections.length === 0 ? (
              <DepotEmptyRow colSpan={8}>No inspection records yet.</DepotEmptyRow>
            ) : (
              inspections.map((item) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-mono font-semibold`}>{item.inspection_no}</td>
                  <td className={depotTdClassName}>{item.container_no}</td>
                  <td className={depotTdClassName}>{item.inspection_type}</td>
                  <td className={depotTdClassName}><Badge variant={item.damage_status === 'sound' ? 'outline' : 'secondary'}>{item.damage_status}</Badge></td>
                  <td className={depotTdClassName}>{item.condition_grade ?? '—'}</td>
                  <td className={depotTdClassName}>{item.photo_count}</td>
                  <td className={depotTdClassName}>{item.inspected_by ?? 'Inspector TBC'}</td>
                  <td className={depotTdClassName}>{item.damage_summary ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <Dialog open={eirOpen} onOpenChange={setEirOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Capture EIR</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <Select
              value={eirForm.visitId}
              onValueChange={(value) => {
                const selected = visitOptions.find((visit) => visit.id === value);
                setEirForm((current) => ({ ...current, visitId: value, depotId: selected?.depot_id ?? current.depotId }));
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select container visit" />
              </SelectTrigger>
              <SelectContent>
                {visitOptions.map((visit) => (
                  <SelectItem key={visit.id} value={visit.id}>{visit.container_no} · {visit.visit_ref}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={eirForm.eirType} onValueChange={(value: 'inbound' | 'outbound') => setEirForm((current) => ({ ...current, eirType: value }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inbound">Inbound</SelectItem>
                <SelectItem value="outbound">Outbound</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Condition Grade" value={eirForm.conditionGrade} onChange={(event) => setEirForm((current) => ({ ...current, conditionGrade: event.target.value }))} />
            <Input placeholder="Signed By" value={eirForm.signedBy} onChange={(event) => setEirForm((current) => ({ ...current, signedBy: event.target.value }))} />
            <Input placeholder="Photo Count" value={eirForm.photoCount} onChange={(event) => setEirForm((current) => ({ ...current, photoCount: event.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEirOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateEir} disabled={saving}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Camera className="mr-1.5 h-4 w-4" />}
              Save EIR
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={inspectionOpen} onOpenChange={setInspectionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Inspection</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <Select
              value={inspectionForm.visitId}
              onValueChange={(value) => {
                const selected = visitOptions.find((visit) => visit.id === value);
                setInspectionForm((current) => ({ ...current, visitId: value, depotId: selected?.depot_id ?? current.depotId }));
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select container visit" />
              </SelectTrigger>
              <SelectContent>
                {visitOptions.map((visit) => (
                  <SelectItem key={visit.id} value={visit.id}>{visit.container_no} · {visit.visit_ref}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={inspectionForm.inspectionType} onValueChange={(value: 'inbound' | 'outbound' | 'yard') => setInspectionForm((current) => ({ ...current, inspectionType: value }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inbound">Inbound</SelectItem>
                <SelectItem value="outbound">Outbound</SelectItem>
                <SelectItem value="yard">Yard</SelectItem>
              </SelectContent>
            </Select>
            <Select value={inspectionForm.damageStatus} onValueChange={(value: 'sound' | 'damaged' | 'repair_required' | 'wash_required') => setInspectionForm((current) => ({ ...current, damageStatus: value }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sound">Sound</SelectItem>
                <SelectItem value="damaged">Damaged</SelectItem>
                <SelectItem value="repair_required">Repair Required</SelectItem>
                <SelectItem value="wash_required">Wash Required</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Condition Grade" value={inspectionForm.conditionGrade} onChange={(event) => setInspectionForm((current) => ({ ...current, conditionGrade: event.target.value }))} />
            <Input placeholder="Damage Summary" value={inspectionForm.damageSummary} onChange={(event) => setInspectionForm((current) => ({ ...current, damageSummary: event.target.value }))} />
            <Input placeholder="Inspected By" value={inspectionForm.inspectedBy} onChange={(event) => setInspectionForm((current) => ({ ...current, inspectedBy: event.target.value }))} />
            <Input placeholder="Photo Count" value={inspectionForm.photoCount} onChange={(event) => setInspectionForm((current) => ({ ...current, photoCount: event.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInspectionOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateInspection} disabled={saving}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ClipboardList className="mr-1.5 h-4 w-4" />}
              Save Inspection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
